import {
  CockroachDBConnectionConfigurationBE,
  Connection,
  ConnectionConfiguration,
  ConnectionConfigurationCreate,
  ConnectionCreate,
} from "src/api/connectApi/types";
import {
  filterSecretsByEnvironment,
  filterUpdatedSecrets,
  findSecret,
  getDefaultSecret,
  markSecrets,
  setSecretDeleted,
  setSecretEnv,
} from "src/connections/model/common";
import {
  CockroachDBConnectionConfig,
  CockroachDBConnectionConfigInputs,
  KeyValuePair,
} from "src/connections/types";

export const getCockroachDBConnectionConfigDefaultValues = (
  env: string | null,
): CockroachDBConnectionConfig => ({
  databaseName: "",
  host: "",
  port: null,
  allowTestInvocations: false,
  authMethod: "username_password",
  hasMTLSEnabled: false,
  mTLSConfig: {
    mode: "verify-full",
    clientCertificate: "",
    clientKey: getDefaultSecret("client_key", env),
    CACertificate: "",
  },
  usernamePasswordConfig: {
    username: getDefaultSecret("username", env),
    password: getDefaultSecret("password", env),
  },
  SSHTunnel: {
    host: "",
    port: 22,
    user: "",
    privateKey: getDefaultSecret("ssh_private_key", env),
  },
  hasSSHTunnelEnabled: false,
  hasRetryEnabled: false,
  retryConfig: undefined,
});

export const getCockroachDBConnectionConfigInputsDefaultValues =
  (): CockroachDBConnectionConfigInputs => ({
    name: "",
    dataRetention: {
      value: 0,
      unit: "days",
    },
    enableNonProdConfigs: false,
    productionConfig: getCockroachDBConnectionConfigDefaultValues(null),
    sandboxConfig: getCockroachDBConnectionConfigDefaultValues("sandbox"),
  });

const convertBEConfigToCockroachDBConnectionConfig = (
  config: ConnectionConfiguration,
  envSecrets: KeyValuePair[],
  env: string | null,
): CockroachDBConnectionConfig => {
  const cockroachConfig = config as CockroachDBConnectionConfigurationBE;
  return {
    databaseName: cockroachConfig.database_name,
    host: cockroachConfig.host,
    port: cockroachConfig.port,
    allowTestInvocations: config.allow_test_invocations ?? false,
    hasMTLSEnabled: cockroachConfig.has_mtls_enabled ?? false,
    mTLSConfig: {
      mode: cockroachConfig.mutual_tls?.mode ?? "verify-full",
      clientCertificate: cockroachConfig.mutual_tls?.client_certificate ?? "",
      clientKey:
        findSecret(envSecrets, "client_key") ??
        getDefaultSecret("client_key", env),
      CACertificate: cockroachConfig.mutual_tls?.ca_certificate ?? "",
    },
    authMethod: cockroachConfig.auth_method,
    usernamePasswordConfig: {
      username:
        findSecret(envSecrets, "username") ?? getDefaultSecret("username", env),
      password:
        findSecret(envSecrets, "password") ?? getDefaultSecret("password", env),
    },
    SSHTunnel: {
      host: cockroachConfig.ssh_tunnel?.host ?? "",
      port: cockroachConfig.ssh_tunnel?.port ?? 22,
      user: cockroachConfig.ssh_tunnel?.user ?? "",
      privateKey:
        findSecret(envSecrets, "ssh_private_key") ??
        getDefaultSecret("ssh_private_key", env),
    },
    hasSSHTunnelEnabled: cockroachConfig.has_ssh_tunnel_enabled ?? false,
    hasRetryEnabled: cockroachConfig.has_retry_enabled ?? false,
    retryConfig: cockroachConfig.retry_config
      ? {
          maxRetries: cockroachConfig.retry_config.max_retries,
          maxBackoff: cockroachConfig.retry_config.max_backoff,
        }
      : undefined,
  };
};

const convertBENonProdEnvConfigToCockroachDBConnectionConfig = (
  nonProdEnvConfigs: Record<string, ConnectionConfiguration>,
  env: string,
  allSecrets: KeyValuePair[],
): CockroachDBConnectionConfig | null => {
  if (!nonProdEnvConfigs?.[env]) return null;
  const envSecrets = filterSecretsByEnvironment(allSecrets, env);
  return convertBEConfigToCockroachDBConnectionConfig(
    nonProdEnvConfigs[env],
    envSecrets,
    env,
  );
};

/**
 * Converts BE CockroachDB connection configuration to FE format.
 * If the provider of the connection is not "cockroachdb", it throws an error.
 */
export const convertBEConnectionToCockroachDBConnectionConfigInputs = (
  connection: Connection,
): CockroachDBConnectionConfigInputs => {
  if (connection.provider !== "cockroachdb")
    throw new Error(
      `Invalid connection type: ${connection.provider} for cockroachdb converter`,
    );

  const defaultValues = getCockroachDBConnectionConfigInputsDefaultValues();
  const nonProdEnvConfigs = connection.non_prod_env_configs;

  const allSecrets = markSecrets(connection.secrets) ?? [];
  const prodSecrets = filterSecretsByEnvironment(allSecrets, null);
  return {
    ...defaultValues,
    name: connection.name,
    dataRetention: connection.data_retention ?? defaultValues.dataRetention,
    enableNonProdConfigs: connection.enable_non_prod_configs,
    productionConfig: convertBEConfigToCockroachDBConnectionConfig(
      connection.configuration,
      prodSecrets,
      null,
    ),
    sandboxConfig:
      nonProdEnvConfigs && nonProdEnvConfigs["sandbox"]
        ? convertBENonProdEnvConfigToCockroachDBConnectionConfig(
            nonProdEnvConfigs,
            "sandbox",
            allSecrets,
          )
        : getCockroachDBConnectionConfigDefaultValues("sandbox"),
  };
};

const extractSecretsFromCockroachDBConnectionConfig = (
  config: CockroachDBConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  if (config.authMethod === "username_password") {
    return [
      setSecretEnv(config.usernamePasswordConfig.username, env),
      setSecretEnv(config.usernamePasswordConfig.password, env),
    ];
  }
  return [];
};

const extractSecretsFromCockroachDBConnectionMTLSConfig = (
  config: CockroachDBConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  if (config.hasMTLSEnabled) {
    return [setSecretEnv(config.mTLSConfig.clientKey, env)];
  } else {
    return [config.mTLSConfig.clientKey]
      .filter((secret) => secret.value !== "")
      .map((s) => setSecretEnv(s, env))
      .map(setSecretDeleted);
  }
};

const extractSecretsFromCockroachDBConnectionSSHTunnelConfig = (
  config: CockroachDBConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  if (config.hasSSHTunnelEnabled && config.SSHTunnel) {
    return [setSecretEnv(config.SSHTunnel.privateKey, env)];
  }
  if (config.SSHTunnel) {
    return [config.SSHTunnel.privateKey]
      .filter((secret) => secret?.value !== "")
      .map((s) => setSecretEnv(s, env))
      .map(setSecretDeleted);
  }
  return [];
};

const extractSecretsFromCockroachDBConnectionConfigInputs = (
  config: CockroachDBConnectionConfigInputs,
): KeyValuePair[] => {
  const secrets = extractSecretsFromCockroachDBConnectionConfig(
    config.productionConfig,
    null,
  );
  secrets.push(
    ...extractSecretsFromCockroachDBConnectionMTLSConfig(
      config.productionConfig,
      null,
    ),
  );
  secrets.push(
    ...extractSecretsFromCockroachDBConnectionSSHTunnelConfig(
      config.productionConfig,
      null,
    ),
  );

  if (config.enableNonProdConfigs && config.sandboxConfig) {
    secrets.push(
      ...extractSecretsFromCockroachDBConnectionConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
    secrets.push(
      ...extractSecretsFromCockroachDBConnectionMTLSConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
    secrets.push(
      ...extractSecretsFromCockroachDBConnectionSSHTunnelConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
  }

  return filterUpdatedSecrets(secrets);
};

const convertCockroachDBConnectionConfigToBEConfiguration = (
  config: CockroachDBConnectionConfig,
): ConnectionConfigurationCreate => {
  return {
    database_name: config.databaseName,
    host: config.host,
    port: config.port || null,
    auth_method: config.authMethod,
    allow_test_invocations: config.allowTestInvocations,
    has_mtls_enabled: config.hasMTLSEnabled,
    mutual_tls: {
      mode: config.mTLSConfig.mode,
      client_certificate: config.mTLSConfig.clientCertificate,
      ca_certificate: config.mTLSConfig.CACertificate,
    },
    has_ssh_tunnel_enabled: config.hasSSHTunnelEnabled,
    ssh_tunnel: {
      host: config.SSHTunnel?.host ?? "",
      port: config.SSHTunnel?.port ?? 22,
      user: config.SSHTunnel?.user ?? "",
    },
    has_retry_enabled: config.hasRetryEnabled,
    retry_config: config.hasRetryEnabled
      ? {
          max_retries: config.retryConfig?.maxRetries ?? 3,
          max_backoff: config.retryConfig?.maxBackoff,
        }
      : undefined,
  };
};

/**
 * Converts FE CockroachDB connection object to BE format.
 */
export const convertCockroachDBConnectionConfigInputsToBEConnection = (
  inputs: CockroachDBConnectionConfigInputs,
): ConnectionCreate => {
  const secrets = extractSecretsFromCockroachDBConnectionConfigInputs(inputs);

  return {
    name: inputs.name,
    is_sandbox: false,
    provider: "cockroachdb",
    configuration: {
      ...convertCockroachDBConnectionConfigToBEConfiguration(
        inputs.productionConfig,
      ),
    },
    secrets,
    data_retention: inputs.dataRetention,
    enable_non_prod_configs: inputs.enableNonProdConfigs,
    non_prod_env_configs:
      inputs.enableNonProdConfigs && inputs.sandboxConfig
        ? {
            sandbox: convertCockroachDBConnectionConfigToBEConfiguration(
              inputs.sandboxConfig,
            ),
          }
        : {},
  };
};
